import { multiFactor, User } from '@firebase/auth'
import {
  CODE_QUERY_PARAM,
  CONTINUE_URL_QUERY_PARAM,
  env,
  MODE_QUERY_PARAM,
  paths,
  REAUTHENTICATE_QUERY_PARAM,
  REAUTHENTICATION_STATUS_QUERY_PARAM,
  REAUTHENTICATION_VARIANT_QUERY_PARAM,
} from 'config'
import {
  Modes,
  ReauthenticationStatus,
  ReauthenticationVariant,
  SsoAuthProvider,
} from 'shared/types'

export const MICROSOFT_PROVIDER_ID = 'microsoft.com' as const

export const AUTH_PROVIDER_LABEL_KEYS: Record<SsoAuthProvider, string> = {
  [SsoAuthProvider.Microsoft]: 'sso.loginTypeMicrosoft',
}

export const getAvailableAuthProviders = (
  canAddSsoUser: boolean
): SsoAuthProvider[] => [...(canAddSsoUser ? [SsoAuthProvider.Microsoft] : [])]

export const OAUTH_PROVIDER_IDS = [MICROSOFT_PROVIDER_ID] as const

export type OAuthProviderId = typeof OAUTH_PROVIDER_IDS[number]

export const hasSignInProvider = (
  user: User | null | undefined,
  providerId: string
): boolean =>
  !!user?.providerData.some(provider => provider.providerId === providerId)

export const hasOAuthSignInProvider = (
  user: User | null | undefined
): boolean =>
  OAUTH_PROVIDER_IDS.some(providerId => hasSignInProvider(user, providerId))

export const isTwoFactorEnabled = (user: User | null | undefined): boolean =>
  !!user && multiFactor(user).enrolledFactors.length > 0

// 2FA is offered only for Blue Bolt accounts; Microsoft accounts are secured by MS
export const needsTwoFactorEnrollment = (user: User): boolean =>
  !env.hideTwoFactorAuth &&
  !hasOAuthSignInProvider(user) &&
  !isTwoFactorEnabled(user)

export const getReauthenticationPath = (variant: ReauthenticationVariant) =>
  `${paths.signIn}?${REAUTHENTICATE_QUERY_PARAM}=true&${REAUTHENTICATION_VARIANT_QUERY_PARAM}=${variant}`

interface ReauthenticationFlags {
  security?: boolean
  deleteAccount?: boolean
  extendSession?: boolean
}

export const getReauthenticationVariant = ({
  security,
  deleteAccount,
  extendSession,
}: ReauthenticationFlags) => {
  if (security) return ReauthenticationVariant.SECURITY
  if (deleteAccount) return ReauthenticationVariant.DELETE_ACCOUNT
  if (extendSession) return ReauthenticationVariant.EXTEND_SESSION
  return null
}

// The session extension goes back to the page it was started from instead
type ReauthenticationVariantWithSuccessPage = Exclude<
  ReauthenticationVariant,
  ReauthenticationVariant.EXTEND_SESSION
>

const REAUTHENTICATION_SUCCESS_PAGE_PATHS: Record<
  ReauthenticationVariantWithSuccessPage,
  string
> = {
  [ReauthenticationVariant.SECURITY]: paths.security,
  [ReauthenticationVariant.DELETE_ACCOUNT]: paths.deleteAccount,
}

export const getReauthenticationSuccessPath = (
  variant: ReauthenticationVariantWithSuccessPage
) =>
  `${REAUTHENTICATION_SUCCESS_PAGE_PATHS[variant]}?${REAUTHENTICATION_STATUS_QUERY_PARAM}=${ReauthenticationStatus.SUCCESS}`

// Path of the page for a link from the "second factor added" email, or null for
// any other URL
export const getRevertSecondFactorLinkPath = ({
  pathname,
  search,
}: Pick<Location, 'pathname' | 'search'>) => {
  const params = new URLSearchParams(search)
  const isRevertSecondFactorLink =
    pathname !== paths.revertSecondFactor &&
    !!params.get(CODE_QUERY_PARAM) &&
    params.get(MODE_QUERY_PARAM) === Modes.RevertSecondFactorAddition

  return isRevertSecondFactorLink
    ? `${paths.revertSecondFactor}${search}`
    : null
}

export const isRevertSecondFactorPage = ({
  pathname,
}: Pick<Location, 'pathname'>) => pathname === paths.revertSecondFactor

const getPathname = (url: string | null) => {
  try {
    return url ? new URL(url).pathname : null
  } catch {
    return null
  }
}

// Path of the page for a link from the reauthentication dialog, or null for any
// other URL. The link may land on the app root, with the page in its continue URL.
export const getReauthenticationLinkPath = ({
  pathname,
  search,
}: Pick<Location, 'pathname' | 'search'>) => {
  const params = new URLSearchParams(search)
  const isReauthenticationLink =
    pathname !== paths.reauthenticateWithLink &&
    !!params.get(CODE_QUERY_PARAM) &&
    params.get(MODE_QUERY_PARAM) === Modes.SignIn &&
    getPathname(params.get(CONTINUE_URL_QUERY_PARAM)) ===
      paths.reauthenticateWithLink

  return isReauthenticationLink
    ? `${paths.reauthenticateWithLink}${search}`
    : null
}
